import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Tooltip } from 'primereact/tooltip';
import { Button } from 'primereact/button';
import { useSelector } from 'react-redux';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Loading from '../../../Loading/loading';
import { AuthContext } from '../../../Header/auth - context';
import '../settings.css';

/* PRIJAVE SA TELEFONA — ko je instalirao mobilnu app i kada se prijavljivao.
   Dva pogleda nad istim podacima:
     • sažetak po nalogu (koliko prijava, prva/poslednja, koliko uređaja)
     • hronološka lista poslednjih prijava
   Izvor je TMS_Mobile.auth.MobilniLoginHistory preko /mobile-operators/logovanja.
   Play Console daje samo zbirne brojke bez imena, pa je ovo jedini pouzdan izvor
   "ko zaista koristi aplikaciju". */

const formatDatumVreme = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const MobilnaLogovanjaTab = () => {
  const { t } = useTranslation();
  const toast = useRef(null);
  const auth = useContext(AuthContext);
  const company = useSelector((state) => state.company);

  const [load, setLoad] = useState(true);
  const [logovanja, setLogovanja] = useState([]);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [filterSazetak, setFilterSazetak] = useState(null);

  const ucitaj = useCallback(() => {
    setLoad(true);
    fetch(process.env.REACT_APP_BACKEND_URL + '/mobile-operators/logovanja', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({ limit: 500 }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((res) => setLogovanja(Array.isArray(res) ? res : []))
      .catch(() => {
        setLogovanja([]);
        toast.current?.show({ severity: 'warn', summary: t('mobLog.greska'), life: 4000 });
      })
      .finally(() => setLoad(false));
  }, [auth.token, t]);

  useEffect(() => { ucitaj(); }, [ucitaj]);

  /* Sažetak po nalogu — grupisanje ide na frontendu jer je skup mali (poslednjih
     500 prijava), pa nema razloga za drugi poziv ka bazi. */
  const sazetak = useMemo(() => {
    const mapa = new Map();
    logovanja.forEach((r) => {
      const kljuc = `${r.tip}:${r.username || r.ime || '?'}`;
      const p = mapa.get(kljuc) || {
        kljuc, ime: r.ime, username: r.username, tip: r.tip,
        prijava: 0, uredjaji: new Set(), prva: null, poslednja: null,
      };
      p.prijava += 1;
      if (r.uredjaj) p.uredjaji.add(r.uredjaj);
      const d = new Date(r.prijava);
      if (!p.prva || d < p.prva) p.prva = d;
      if (!p.poslednja || d > p.poslednja) p.poslednja = d;
      mapa.set(kljuc, p);
    });
    return [...mapa.values()]
      .map((p) => ({ ...p, uredjaja: p.uredjaji.size }))
      .sort((a, b) => b.poslednja - a.poslednja);
  }, [logovanja]);

  const exportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Prijave sa telefona');
    const headers = [t('mobLog.korisnik'), t('mobLog.username'), t('mobLog.tipNaloga'),
      t('mobLog.vremePrijave'), t('mobLog.uredjaj')];
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
      cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    });
    logovanja.forEach((r) => {
      const row = worksheet.addRow([r.ime || '', r.username || '', r.tip || '',
        formatDatumVreme(r.prijava), r.uredjaj || '']);
      row.eachCell((cell) => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
    });
    worksheet.columns.forEach((col) => {
      let max = 10;
      col.eachCell((cell) => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
      col.width = max + 2;
    });
    const dt = new Date().toLocaleString('sr-RS', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      timeZone: 'Europe/Belgrade',
    }).replace(/,/g, '');
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Prijave sa telefona - ${company?.companyName || ''} - ${dt}.xlsx`);
  };

  const zaglavljeSazetka = (
    <div className="export-buttons fakture-stavke-header">
      <div className="fakture-stavke-radnje">
        <strong>{t('mobLog.poNalogu')}</strong>
        <Button type="button" icon="pi pi-refresh" rounded text onClick={ucitaj} data-pr-tooltip={t('mobLog.osvezi')} />
      </div>
      <input type="search" onInput={(e) => setFilterSazetak(e.target.value)}
             placeholder={t('nova.phPretraga')} className="p-inputtext p-component" />
    </div>
  );

  const zaglavljeIstorije = (
    <div className="export-buttons fakture-stavke-header">
      <div className="fakture-stavke-radnje">
        <strong>{t('mobLog.hronologija')}</strong>
        <Button type="button" icon="pi pi-file-excel" severity="success" rounded
                onClick={exportExcel} data-pr-tooltip="XLS" />
      </div>
      <input type="search" onInput={(e) => setGlobalFilter(e.target.value)}
             placeholder={t('nova.phPretraga')} className="p-inputtext p-component" />
    </div>
  );

  return (
    <div id="setting_main">
      {load && <Loading />}
      <Toast ref={toast} />

      <div className="setting_table">
        <Card>
          <Tooltip target=".export-buttons>button" position="bottom" />
          <DataTable
            value={sazetak}
            paginator rows={10}
            globalFilter={filterSazetak}
            scrollable scrollHeight="300px"
            resizableColumns columnResizeMode="expand"
            className="turePregledTable stavke-tabela"
            header={zaglavljeSazetka}
            emptyMessage={t('mobLog.nemaPodataka')}
          >
            <Column field="ime" header={t('mobLog.korisnik')} sortable style={{ minWidth: '190px' }}
                    body={(r) => r.ime || r.username || '—'} />
            <Column field="username" header={t('mobLog.username')} sortable style={{ minWidth: '150px' }} />
            <Column field="tip" header={t('mobLog.tipNaloga')} sortable style={{ minWidth: '110px' }} />
            <Column field="prijava" header={t('mobLog.brojPrijava')} sortable style={{ minWidth: '110px' }} />
            <Column field="uredjaja" header={t('mobLog.uredjaja')} sortable style={{ minWidth: '110px' }} />
            <Column field="prva" header={t('mobLog.prvaPrijava')} sortable style={{ minWidth: '160px' }}
                    body={(r) => formatDatumVreme(r.prva)} />
            <Column field="poslednja" header={t('mobLog.poslednjaPrijava')} sortable style={{ minWidth: '160px' }}
                    body={(r) => formatDatumVreme(r.poslednja)} />
          </DataTable>
        </Card>

        <Card style={{ marginTop: '1rem' }}>
          <DataTable
            value={logovanja}
            paginator rows={15}
            globalFilter={globalFilter}
            scrollable scrollHeight="420px"
            resizableColumns columnResizeMode="expand"
            className="turePregledTable stavke-tabela"
            header={zaglavljeIstorije}
            emptyMessage={t('mobLog.nemaPodataka')}
          >
            <Column field="prijava" header={t('mobLog.vremePrijave')} sortable style={{ minWidth: '160px' }}
                    body={(r) => formatDatumVreme(r.prijava)} />
            <Column field="ime" header={t('mobLog.korisnik')} sortable style={{ minWidth: '190px' }}
                    body={(r) => r.ime || r.username || '—'} />
            <Column field="username" header={t('mobLog.username')} sortable style={{ minWidth: '150px' }} />
            <Column field="tip" header={t('mobLog.tipNaloga')} sortable style={{ minWidth: '110px' }} />
            <Column field="uredjaj" header={t('mobLog.uredjaj')} style={{ minWidth: '170px' }}
                    body={(r) => (r.uredjaj ? String(r.uredjaj).slice(0, 18) : '—')} />
          </DataTable>
        </Card>
      </div>
    </div>
  );
};

export default MobilnaLogovanjaTab;
